import { TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ICampaignContent } from '../../../../../model/briefing/step2/ICampaignContent';
import { IParams } from '../../../../../model/shared/IParams';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { InfoBox } from '../../../../../shared/components/alert/InfoBox';
import { CustomAutocomplete } from '../../../../../shared/components/Autocomplete';
import TextEditor from '../../../../../shared/components/TextEditor';
import { UploadImage } from '../../../../../shared/components/UploadImage';
import { clearHtmlFromString, oneStepUp } from '../../../../../shared/functions/Functions';
import { IAllStates } from '../../../../../store/rootReducer';
import { ErrorToast, SuccessToast } from '../../../../../utils/toasters';
import TooltipPackage from '../../../../shared/packages/components/TooltipPackage';
import { BriefingService } from '../service';
import AiModal from './shared/AiModal';
import { CampaignOverviewCard } from './shared/CampaignOverviewCard';
import { ProgressBarCampaign } from './shared/ProgressBarCampaign';
import { StepperRouting } from './shared/StepperRouting';
import CardLayout from '../../../../../shared/components/card/CardLayout';

export const StepTwo = () => {
    const { id } = useParams<keyof IParams>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [isAiOpened, setIsAiOpened] = useState(false);
    const [currentContent, setCurrentContent] = useState<ICampaignContent>();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [triggerUpdate, setTriggerUpdate] = useState(false);
    const [haveMissingDataModal, setHaveMissingDataModal] = useState(false);
    const [isNextClicked, setIsNextClicked] = useState(false);
    const { company } = useSelector((state: IAllStates) => state.auth.user);
    const [files, setFiles] = useState<Blob[] | MediaSource[]>();
    const [selectedAi, setSelectedAi] = useState<'product' | 'briefing' | ''>('');
    const [isBriefingComplete, setIsBriefingComplete] = useState(true);

    useEffect(() => {
        !!id &&
            BriefingService.getBriefingByStep(id ?? '', 'second')
                .then(response => {
                    setCurrentContent(response);
                    setIsBriefingComplete(response?.isBriefingComplete);
                })
                .catch(error => ErrorToast(error));
    }, [id, triggerUpdate]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setCurrentContent(prev => ({ ...prev, [name]: value }));
    };

    const changeTextFieldHandler = (value: string, name: string) => {
        setCurrentContent(prev => ({ ...prev, [name]: value }));
    };

    const addMissingTag = (data: string[], key: string) => {
        const splitKey = key === 'hashtags' ? '#' : '@';
        const tempSet = new Set<string>();

        data.forEach(str => {
            let formattedTag = str.trim().replace(/ /g, '_');

            if (!formattedTag.startsWith(splitKey)) {
                formattedTag = `${splitKey}${formattedTag}`;
            }

            tempSet.add(formattedTag); // Use Set to avoid duplicates
        });

        return Array.from(tempSet);
    };
    const changeParamsHandler = (paramKey: string, paramValue: string | string[] | number) => {
        setCurrentContent(prev => ({ ...prev, [paramKey]: addMissingTag(paramValue as string[], paramKey).join(' ') }));
    };

    const isAdDescriptionInvalid = currentContent?.adDescription
        ? !clearHtmlFromString(currentContent?.adDescription)?.length
        : !currentContent?.adDescription;

    const isDetailsInvalid = currentContent?.details
        ? !clearHtmlFromString(currentContent?.details)?.length
        : !currentContent?.details;

    const submitHandler = () => {
        setIsSubmitted(true);
        const isFormInvalid = isDetailsInvalid || isAdDescriptionInvalid;
        const haveMissingData =
            !currentContent?.hashtags ||
            !currentContent?.url ||
            (!currentContent?.campaignImages?.length && !files?.length);
        if (isFormInvalid) {
            return;
        }
        if (haveMissingData && !haveMissingDataModal) {
            setHaveMissingDataModal(true);
            return;
        }
        const formData = new FormData();
        if (files?.length) {
            for (const item of files) {
                formData.append('files', item as string | Blob);
            }
        }
        formData.append('body', JSON.stringify(currentContent));
        if (currentContent?.id) {
            setIsNextClicked(true);
            BriefingService.updateBriefingStepTwo(String(currentContent?.id), formData)
                .then(response => {
                    if (response) {
                        oneStepUp(navigate, id ?? '', response.isBriefingComplete, response.briefingStep);
                        SuccessToast(`${t('general.alert.success_updated_campaign_step')}`);
                    }
                })
                .catch(error => ErrorToast(error))
                .finally(() => {
                    setIsNextClicked(false);
                });
        }
    };

    const deleteSelectedImage = (selectedId: string) => {
        if (selectedId) {
            BriefingService.deleteBriefingImageByStep('second/image', selectedId)
                .then(response => {
                    if (response === '') {
                        SuccessToast(`${t('general.alert.success_deleted_image')}`);
                        setTriggerUpdate(prev => !prev);
                    }
                })
                .catch(error => ErrorToast(error));
        }
    };
    return (
        <>
            <AiModal
                isProductAi={selectedAi === 'product'}
                show={isAiOpened}
                setCurrentContent={setCurrentContent}
                closeModal={() => setIsAiOpened(false)}
            />
            <ConfirmationModal
                title={t('general.confirm')}
                description={t('brand.campaign.briefing.step2.areYouSure')}
                show={haveMissingDataModal}
                action={submitHandler}
                closeModal={() => setHaveMissingDataModal(false)}
            />
            <Row className="mb-5 mb-md-0">
                <Col xl={8} className="mb-sm-0 mb-2">
                    {!isBriefingComplete ? (
                        <div className="mb-4">
                            <ProgressBarCampaign title={t('general.tabs.step2')} percent={15} />
                        </div>
                    ) : null}
                    <div>
                        <h5 className="mb-4 font-weight-bold">{t('brand.campaign.briefing.step2.title')}</h5>
                    </div>

                    <CardLayout>
                        <div className="mb-3 mb-md-0">
                            <h6 className="mb-1 font-weight-bold">
                                {t('brand.campaign.briefing.step2.promote_title')}
                            </h6>
                            <span className="text-muted">{t('brand.campaign.briefing.step2.promote_description')}</span>
                        </div>
                        <div className="d-flex align-items-md-start justify-content-between flex-column flex-md-row">
                            {currentContent && (
                                <TextEditor
                                    state={currentContent?.details ?? ''}
                                    classes="w-100"
                                    hasError={isSubmitted && isDetailsInvalid}
                                    placeholder={t('brand.campaign.briefing.step2.product_promote_empty_state')}
                                    changeTextFieldHandler={value => changeTextFieldHandler(value, 'details')}
                                />
                            )}
                            <div className="d-flex flex-column ml-md-3 mt-4 mt-md-0 col-md-3 col-12 px-0">
                                <TooltipPackage
                                    trigger="click"
                                    show={!company?.package?.allowBriefingAI}
                                    key="ai-1"
                                    description="noAiBriefing"
                                >
                                    <div>
                                        <InfoBox
                                            customIcon="/icons/img-ai-icon.svg"
                                            variant="primary"
                                            title={t('brand.campaign.briefing.step2.ai_product_title')}
                                            text={t('brand.campaign.briefing.step2.ai_product_description')}
                                            buttonName={t('general.button.generate')}
                                            disabled={!company?.package?.allowBriefingAI}
                                            action={() => {
                                                if (company?.package?.allowBriefingAI) {
                                                    setIsAiOpened(true);
                                                    setSelectedAi('product');
                                                }
                                            }}
                                        />
                                    </div>
                                </TooltipPackage>
                                <InfoBox
                                    variant="info"
                                    className="mb-md-0"
                                    title="influence.vision Feedback"
                                    text={t('brand.campaign.briefing.step2.feedback_boxes_ai_product')}
                                    buttonName=""
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="mb-3 mb-md-0">
                                <h6 className="mt-3">{t('brand.campaign.briefing.step2.content_creator_title')}</h6>
                                <span className="text-muted">
                                    {t('brand.campaign.briefing.step2.content_creator_description')}
                                </span>
                            </div>
                            <div className="d-flex align-items-md-start justify-content-between flex-column flex-md-row">
                                {currentContent?.id && (
                                    <TextEditor
                                        state={currentContent?.adDescription}
                                        key={currentContent?.id}
                                        hasError={isSubmitted && isAdDescriptionInvalid}
                                        classes="w-100"
                                        placeholder={t('brand.campaign.briefing.step2.content_creator_empty_state')}
                                        changeTextFieldHandler={value => changeTextFieldHandler(value, 'adDescription')}
                                    />
                                )}
                                <div className="d-flex flex-column ml-md-3 mt-4 mt-xl-0 col-md-3 col-12 px-0">
                                    <TooltipPackage
                                        trigger="click"
                                        placement="top"
                                        show={!company?.package?.allowBriefingAI}
                                        key="ai-2"
                                        description="noAiBriefing"
                                    >
                                        <div>
                                            <InfoBox
                                                customIcon="/icons/img-ai-icon.svg"
                                                variant="primary"
                                                title={t('brand.campaign.briefing.step2.ai_briefing_generator')}
                                                text={t(
                                                    'brand.campaign.briefing.step2.ai_briefing_generator_description'
                                                )}
                                                buttonName={t('general.button.generate')}
                                                disabled={!company?.package?.allowBriefingAI}
                                                action={() => {
                                                    if (company?.package?.allowBriefingAI) {
                                                        setIsAiOpened(true);
                                                        setSelectedAi('briefing');
                                                    }
                                                }}
                                            />
                                        </div>
                                    </TooltipPackage>
                                    <InfoBox
                                        variant="info"
                                        className="mb-md-0"
                                        title="influence.vision Feedback"
                                        text={t('brand.campaign.briefing.step2.feedback_boxes_ai_briefing')}
                                        buttonName=""
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="d-flex flex-column flex-md-row align-items-start">
                            <div className="col-lg-6 px-0">
                                <div>
                                    <h6 className="mb-0">
                                        {t('brand.campaign.briefing.step2.relevant_hashtags')} &{' '}
                                        {t('influencer.offer_management.modal.tagging')}
                                    </h6>
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step2.relevant_hashtags_description')}
                                    </span>
                                    {currentContent && (
                                        <CustomAutocomplete
                                            classes="mt-2"
                                            isMultiple={true}
                                            isFreeSolo={true}
                                            dataArray={[]}
                                            defaultArray={
                                                currentContent?.hashtags !== '' && currentContent?.hashtags
                                                    ? (currentContent?.hashtags?.split(' ') as string[])
                                                    : []
                                            }
                                            isWithImage={false}
                                            inputLabel="hashtag"
                                            inputLabelTranslation={`Hashtags`}
                                            queryFilterKey="hashtags"
                                            handleChangeParams={changeParamsHandler}
                                        />
                                    )}
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step2.relevant_hashtags_warning', {
                                            value: 'hashtag',
                                        })}
                                    </span>
                                </div>
                                <div className="mt-4">
                                    {currentContent && (
                                        <CustomAutocomplete
                                            classes="mt-2"
                                            isMultiple={true}
                                            isFreeSolo={true}
                                            dataArray={[]}
                                            defaultArray={
                                                currentContent?.profileTags !== '' && currentContent?.profileTags
                                                    ? (currentContent?.profileTags?.split(' ') as string[])
                                                    : []
                                            }
                                            isWithImage={false}
                                            inputLabel="profileTags"
                                            inputLabelTranslation={`Tagging`}
                                            queryFilterKey="profileTags"
                                            handleChangeParams={changeParamsHandler}
                                        />
                                    )}
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step2.relevant_hashtags_warning', {
                                            value: 'tagging',
                                        })}
                                    </span>
                                </div>
                                <div className="mt-4 mb-3 mb-md-0">
                                    <h6 className="mb-0">{t('brand.campaign.briefing.step2.inspiration_link')}</h6>
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step2.inspiration_link_desc')}
                                    </span>
                                    <TextField
                                        focused={!!currentContent?.url?.length}
                                        name="url"
                                        value={currentContent?.url}
                                        onChange={changeHandler}
                                        label="Link"
                                        variant="outlined"
                                        className="w-100 mt-1"
                                        size="small"
                                    />
                                </div>
                            </div>

                            <div className="col-lg-6 px-0 mb-md-0 mb-4 ml-md-3 ">
                                <div className="ml-md-3 mb-2">
                                    <h6 className="mb-0">{t('brand.campaign.briefing.step2.moodboard')}</h6>
                                    <span className="text-muted">
                                        {t('brand.campaign.briefing.step2.moodboard_desc')}
                                    </span>
                                </div>

                                <div>
                                    <UploadImage
                                        saveFile={setFiles}
                                        deleteSelectedImage={deleteSelectedImage}
                                        previewImage={currentContent?.campaignImages}
                                        isMultiple={true}
                                        isFullHeight={true}
                                        acceptedFormats="image/jpeg, image/png, image/jpg, application/pdf"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-md-4">
                            <StepperRouting
                                submitStepHandler={submitHandler}
                                isNextDisabled={isNextClicked}
                                status={currentContent?.status}
                            />
                        </div>
                    </CardLayout>
                </Col>
                <Col xl={4} className="position-relative pr-md-4 mt-md-4 mt-xl-0">
                    <CampaignOverviewCard />
                </Col>
            </Row>
        </>
    );
};
